import { useNavigate } from 'react-router-dom'
import { Box, Button, Text } from '@chakra-ui/react'

import { GetInterviewResponse } from '@pkg/types/api/responses'
import { Candidate } from '@pkg/types/interview'
import { Role } from '@pkg/types/role'

import { queryClient } from '@/react-query/client'
import {
  SHORT_INTERVIEWS_LIST_KEY,
  useDeleteInterview,
} from '@/react-query/interview/interview'
import ArrowLeftLong from '@/views/assets/ArrowLeftLong'
import { INTERVIEW_TYPE_FILTER_KEY } from '@/views/screens/Home/FilterHeader/const'
import { getAudioChunks } from '@/views/screens/Interview/context/AudioRecorderContext/indexed-db'

const isEmptyInterview = (interview: GetInterviewResponse['interview']) => {
  return (
    !interview.candidateId &&
    !interview.roleId &&
    !interview.fullTranscript &&
    !interview.shortTranscript &&
    !interview.isFullTranscriptGenerating &&
    !interview.recallId &&
    !interview.recallStatus
  )
}

export const DynamicHeader = ({
  role,
  isLoadingExistingInterview,
  candidate,
  interview,
  isRecording,
  isFileUploading,
  fileUploadProgress,
  stopRecordingNoUpload,
}: {
  role: Role | undefined | null
  candidate: Candidate | undefined | null
  isLoadingExistingInterview: boolean
  interview: GetInterviewResponse['interview'] | undefined | null
  isRecording: boolean
  isFileUploading: boolean
  fileUploadProgress: number | undefined | null
  stopRecordingNoUpload: () => void
}) => {
  const deleteInterview = useDeleteInterview()
  const navigate = useNavigate()

  return (
    <Box
      flex={1}
      minW={0}
      ml={{
        base: 3,
        md: 6,
      }}
      display={'flex'}
      alignItems={{
        base: 'flex-start',
        md: 'center',
      }}
      justifyContent={'flex-start'}
      flexWrap={'wrap'}
      gap={{
        base: 3,
        md: 4,
      }}
    >
      <Box
        display={'flex'}
        alignItems={{
          base: 'center',
          md: 'baseline',
        }}
        flexWrap={'wrap'}
        gap={{
          base: 2,
          md: 3,
        }}
        minW={0}
        flex={1}
      >
        <Button
          as={'div'}
          variant={'brand-solid-white'}
          rounded={'full'}
          px={{
            base: '0.75rem',
            md: '1.15rem',
          }}
          minW={'fit-content'}
          onClick={async () => {
            const hasChunks = await getAudioChunks(interview?.id || '')
            if (
              interview &&
              isEmptyInterview(interview) &&
              !isRecording &&
              !isFileUploading &&
              !fileUploadProgress &&
              !hasChunks
            ) {
              await deleteInterview.mutateAsync('auto_empty')
            }
            queryClient.invalidateQueries({
              queryKey: [SHORT_INTERVIEWS_LIST_KEY],
            })
            localStorage.setItem(
              INTERVIEW_TYPE_FILTER_KEY,
              interview?.type || 'NORMAL',
            )
            stopRecordingNoUpload()
            navigate('/home')
          }}
          cursor={'pointer'}
          isLoading={deleteInterview.isPending}
          whiteSpace={'nowrap'}
          size={{
            base: 'sm',
            md: 'md',
          }}
        >
          <ArrowLeftLong />
          <Box
            as={'span'}
            ml={2}
            display={{ base: 'none', sm: 'inline', md: 'none', lg: 'inline' }}
          >
            All interviews
          </Box>
        </Button>
        <Box
          data-testid="interview-title"
          fontWeight={'bold'}
          display={{
            base: 'none',
            md: 'block',
          }}
          // Nudge the title right so its left edge lines up with the left edge
          // of the tabs / summary cards below (which sit in the right column,
          // offset by the sidebar width). Only needed at `xl`: there the wider
          // 22rem sidebar pushes that column past the title. At `lg` the 17rem
          // sidebar keeps the column left of the "All interviews" button, so a
          // nudge there would only overlap it; below `lg` the layout is stacked.
          // Measured offset ~33px; see e2e/interview-title-align.spec.ts.
          ml={{
            base: 0,
            xl: 8,
          }}
          minW={0}
          flex={1}
        >
          <Text
            fontSize={{
              md: 'lg',
              xl: '2xl',
            }}
            noOfLines={1}
          >
            {isLoadingExistingInterview
              ? ''
              : candidate?.fullName
                ? `${candidate.fullName}${
                    role?.jobTitle ? ` × ${role.jobTitle}` : ''
                  }`
                : interview?.meetingName
                  ? interview.meetingName
                  : interview && !isEmptyInterview(interview)
                    ? 'Interview'
                    : 'new interview'}
          </Text>
        </Box>
      </Box>
      {/* PDF export button moved to Toolbox (next to Copy Full Scorecard) */}
    </Box>
  )
}
